// src/hooks/useProjectTasks.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useStableAuth } from '@/hooks/useStableAuth';
import { toast } from 'sonner';
import type { ProjectTask, ProjectTaskStatus, ProjectTaskPriority } from '@/types/project';

export const projectTaskKeys = {
  all: ['project-tasks'] as const,
  byProject: (projectId: string, showCompleted: boolean) =>
    [...projectTaskKeys.all, projectId, { showCompleted }] as const,
};

export function useProjectTasks(
  projectId: string | undefined,
  showCompleted = false
) {
  const { userId, companyId } = useStableAuth();
  const queryClient = useQueryClient();

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: projectTaskKeys.all });
  };

  // ── Listagem de tarefas do projeto ─────────────────────────────────────────
  const tasksQuery = useQuery({
    queryKey: projectTaskKeys.byProject(projectId ?? '', showCompleted),
    queryFn: async (): Promise<ProjectTask[]> => {
      const { data, error } = await supabase
        .from('task_project_memberships')
        .select(`
          id,
          task_id,
          project_id,
          project_section_id,
          position,
          added_by,
          added_at,
          tasks!task_project_memberships_task_id_fkey(
            id,
            title,
            description,
            status,
            priority,
            due_date,
            assigned_to,
            created_by,
            is_private,
            is_system_generated,
            section_id,
            origin_type,
            origin_context,
            delivery_id,
            maintenance_alert_id,
            created_at,
            updated_at,
            completed_at,
            deleted_at,
            custom_fields
          )
        `)
        .eq('project_id', projectId!)
        .order('position', { ascending: true });

      if (error) throw error;

      return (data ?? [])
        .filter((row: any) => {
          const task = row.tasks;
          if (!task || task.deleted_at) return false;
          if (!showCompleted && task.status === 'completed') return false;
          return true;
        })
        .map((row: any) => ({
          task_id: row.task_id,
          project_id: row.project_id,
          project_section_id: row.project_section_id,
          position: row.position,
          id: row.tasks.id,
          title: row.tasks.title,
          description: row.tasks.description,
          status: row.tasks.status as ProjectTaskStatus,
          priority: row.tasks.priority as ProjectTaskPriority | null,
          due_date: row.tasks.due_date,
          assigned_to: row.tasks.assigned_to,
          created_by: row.tasks.created_by,
          is_private: row.tasks.is_private,
          is_system_generated: row.tasks.is_system_generated,
          section_id: row.tasks.section_id,
          origin_type: row.tasks.origin_type,
          origin_context: row.tasks.origin_context ?? null,
          delivery_id: row.tasks.delivery_id,
          maintenance_alert_id: row.tasks.maintenance_alert_id,
          created_at: row.tasks.created_at,
          updated_at: row.tasks.updated_at,
          completed_at: row.tasks.completed_at,
          custom_fields: row.tasks.custom_fields ?? null,
          assignee_name: null,
          assignee_avatar: null,
        }));
    },
    enabled: !!projectId && !!userId && !!companyId,
  });

  // ── Criar tarefa no projeto ────────────────────────────────────────────────
  const createTaskMutation = useMutation({
    mutationFn: async ({
      sectionId,
      title,
      assignedTo,
      dueDate,
      priority,
    }: {
      sectionId: string;
      title: string;
      assignedTo?: string;
      dueDate?: string;
      priority?: string;
    }): Promise<string> => {
      const { data, error } = await supabase.rpc('create_task_in_project', {
        p_project_id: projectId!,
        p_section_id: sectionId,
        p_title: title,
        p_assigned_to: assignedTo ?? null,
        p_due_date: dueDate ?? null,
        p_priority: priority ?? null,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: () => {
      invalidate();
      toast.success('Tarefa criada no projeto.');
    },
    onError: (error: Error) => {
      console.error('Erro ao criar tarefa:', error);
      toast.error('Erro ao criar tarefa.');
    },
  });

  // ── Mover tarefa entre seções (drag & drop) ────────────────────────────────
  const moveTaskMutation = useMutation({
    mutationFn: async ({
      taskId,
      targetSectionId,
      newPosition,
    }: {
      taskId: string;
      targetSectionId: string;
      newPosition: number;
    }): Promise<void> => {
      const { error } = await supabase
        .from('task_project_memberships')
        .update({
          project_section_id: targetSectionId,
          position: newPosition,
        })
        .eq('task_id', taskId)
        .eq('project_id', projectId!);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
    },
    onError: (error: Error) => {
      console.error('Erro ao mover tarefa:', error);
      toast.error('Erro ao mover tarefa.');
    },
  });

  // ── Remover tarefa do projeto (sem deletar a tarefa em si) ─────────────────
  const removeFromProjectMutation = useMutation({
    mutationFn: async (taskId: string): Promise<void> => {
      const { error } = await supabase
        .from('task_project_memberships')
        .delete()
        .eq('task_id', taskId)
        .eq('project_id', projectId!);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast.success('Tarefa removida do projeto.');
    },
    onError: (error: Error) => {
      console.error('Erro ao remover tarefa do projeto:', error);
      toast.error('Erro ao remover tarefa do projeto.');
    },
  });

  // Agrupa tarefas por seção para o Kanban
  const tasksBySection = (sectionId: string | null): ProjectTask[] => {
    return (tasksQuery.data ?? []).filter(
      (t) => t.project_section_id === sectionId
    );
  };

  return {
    tasks: tasksQuery.data ?? [],
    isLoading: tasksQuery.isLoading,
    tasksBySection,
    createTask: createTaskMutation.mutateAsync,
    isCreatingTask: createTaskMutation.isPending,
    moveTask: moveTaskMutation.mutateAsync,
    removeFromProject: removeFromProjectMutation.mutateAsync,
  };
}
